import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export function useDashboardData() {
  const [todaysOpportunity, setTodaysOpportunity] = useState(null);
  const [opportunities, setOpportunities] = useState([]);
  const [settings, setSettings] = useState(null);
  const [stats, setStats] = useState(null);
  const [trends, setTrends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [watchlist, setWatchlist] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  const [portfolioSummary, setPortfolioSummary] = useState(null);
  const [trackingModal, setTrackingModal] = useState(null);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [performance, setPerformance] = useState(null);
  const [dealsData, setDealsData] = useState(null);
  const [exportingPdf, setExportingPdf] = useState(false);

  const watchlistIds = new Set(watchlist.map(w => w.opportunity_id));
  const portfolioOppIds = new Set(portfolio.map(p => p.opportunity_id));

  const fetchData = useCallback(async () => {
    try {
      const timeout = new Promise((_, reject) =>
        setTimeout(() => reject(new Error("Request timeout")), 15000)
      );

      const fetchPromise = Promise.all([
        axios.get(`${API}/opportunity/today`),
        axios.get(`${API}/opportunities?limit=20`),
        axios.get(`${API}/settings`),
        axios.get(`${API}/stats`),
        axios.get(`${API}/trends`),
        axios.get(`${API}/watchlist`),
        axios.get(`${API}/portfolio`),
        axios.get(`${API}/portfolio/summary`),
        axios.get(`${API}/performance`),
        axios.get(`${API}/deals-log`),
      ]);

      const [
        oppRes,
        historyRes,
        settingsRes,
        statsRes,
        trendsRes,
        watchlistRes,
        portfolioRes,
        summaryRes,
        perfRes,
        dealsRes,
      ] = await Promise.race([fetchPromise, timeout]);

      setTodaysOpportunity(oppRes.data);
      setOpportunities(historyRes.data || []);
      setSettings(settingsRes.data);
      setStats(statsRes.data);
      setTrends(trendsRes.data || []);
      setWatchlist(watchlistRes.data || []);
      setPortfolio(portfolioRes.data || []);
      setPortfolioSummary(summaryRes.data);
      setPerformance(perfRes.data);
      setDealsData(dealsRes.data);

      if (oppRes.data) {
        setSelectedOpportunity(oppRes.data);
      }
    } catch (error) {
      if (error.message !== "Request timeout") {
        toast.error("Failed to load data");
      }
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- API/axios/toast are module-level constants; all set* are stable React setState refs
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const refreshSummary = async () => {
    const summaryRes = await axios.get(`${API}/portfolio/summary`);
    setPortfolioSummary(summaryRes.data);
  };

  const generateOpportunity = async (category = null) => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/generate`, { category });
      setTodaysOpportunity(res.data);
      setSelectedOpportunity(res.data);
      setOpportunities((prev) => [
        res.data,
        ...prev.filter((o) => o.id !== res.data.id),
      ]);
      toast.success("New opportunity generated!");

      const statsRes = await axios.get(`${API}/stats`);
      setStats(statsRes.data);
    } catch {
      toast.error("Failed to generate opportunity");
    } finally {
      setGenerating(false);
    }
  };

  const updateSettings = async (updates) => {
    setSavingSettings(true);
    try {
      const res = await axios.put(`${API}/settings`, updates);
      setSettings(res.data);
      toast.success("Settings updated");
    } catch {
      toast.error("Failed to update settings");
    } finally {
      setSavingSettings(false);
    }
  };

  const toggleWatchlist = async (opportunity) => {
    const isWatchlisted = watchlistIds.has(opportunity.id);
    if (isWatchlisted) {
      const item = watchlist.find(
        (w) => w.opportunity_id === opportunity.id
      );
      if (item) {
        try {
          await axios.delete(`${API}/watchlist/${item.id}`);
          setWatchlist((prev) => prev.filter((w) => w.id !== item.id));
          toast.success("Removed from watchlist");
        } catch {
          toast.error("Failed to remove from watchlist");
        }
      }
    } else {
      try {
        const res = await axios.post(`${API}/watchlist`, {
          opportunity_id: opportunity.id,
          notes: "",
        });
        setWatchlist((prev) => [res.data, ...prev]);
        toast.success("Added to watchlist");
      } catch (err) {
        if (err.response?.status === 409) {
          toast.info("Already in watchlist");
        } else {
          toast.error("Failed to add to watchlist");
        }
      }
    }
  };

  const updateWatchlistItem = async (itemId, updates) => {
    try {
      const res = await axios.put(`${API}/watchlist/${itemId}`, updates);
      setWatchlist((prev) =>
        prev.map((w) => (w.id === itemId ? res.data : w))
      );
      toast.success("Watchlist updated");
    } catch {
      toast.error("Failed to update");
    }
  };

  const removeWatchlistItem = async (itemId) => {
    try {
      await axios.delete(`${API}/watchlist/${itemId}`);
      setWatchlist((prev) => prev.filter((w) => w.id !== itemId));
      toast.success("Removed from watchlist");
    } catch {
      toast.error("Failed to remove");
    }
  };

  const viewOpportunity = (opportunityId) => {
    const opp = opportunities.find((o) => o.id === opportunityId);
    if (opp) {
      setSelectedOpportunity(opp);
      setActiveTab("dashboard");
    } else {
      axios
        .get(`${API}/opportunity/${opportunityId}`)
        .then((res) => {
          setSelectedOpportunity(res.data);
          setActiveTab("dashboard");
        })
        .catch(() => toast.error("Opportunity not found"));
    }
  };

  const trackInPortfolio = async (
    watchlistItem,
    entryPrice,
    amountInvested,
    notes
  ) => {
    try {
      const res = await axios.post(`${API}/portfolio`, {
        opportunity_id: watchlistItem.opportunity_id,
        watchlist_item_id: watchlistItem.id,
        entry_price: entryPrice,
        amount_invested: amountInvested,
        notes,
      });
      setPortfolio((prev) => [res.data, ...prev]);
      await refreshSummary();
      setTrackingModal(null);
      toast.success("Investment tracked in portfolio!");
    } catch (err) {
      if (err.response?.status === 409) {
        toast.info("Already tracked in portfolio");
      } else {
        toast.error("Failed to add to portfolio");
      }
    }
  };

  const updatePortfolioItem = async (itemId, updates) => {
    try {
      const res = await axios.put(`${API}/portfolio/${itemId}`, updates);
      setPortfolio((prev) =>
        prev.map((p) => (p.id === itemId ? res.data : p))
      );
      await refreshSummary();
      toast.success("Portfolio updated");
    } catch {
      toast.error("Failed to update");
    }
  };

  const removePortfolioItem = async (itemId) => {
    try {
      await axios.delete(`${API}/portfolio/${itemId}`);
      setPortfolio((prev) => prev.filter((p) => p.id !== itemId));
      await refreshSummary();
      toast.success("Removed from portfolio");
    } catch {
      toast.error("Failed to remove");
    }
  };

  const exportPortfolioPdf = async () => {
    setExportingPdf(true);
    try {
      const res = await axios.get(`${API}/portfolio/export-pdf`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "myqairo_portfolio_report.pdf");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Portfolio report downloaded!");
    } catch {
      toast.error("Failed to export PDF");
    } finally {
      setExportingPdf(false);
    }
  };

  const testWhatsApp = async () => {
    try {
      const res = await axios.post(`${API}/notifications/whatsapp/test`);
      toast.success(res.data.message || "WhatsApp test sent (simulated)");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to test WhatsApp");
    }
  };

  return {
    todaysOpportunity,
    opportunities,
    settings,
    stats,
    trends,
    loading,
    generating,
    selectedOpportunity,
    setSelectedOpportunity,
    savingSettings,
    watchlist,
    watchlistIds,
    portfolio,
    portfolioOppIds,
    portfolioSummary,
    trackingModal,
    setTrackingModal,
    activeTab,
    setActiveTab,
    performance,
    dealsData,
    exportingPdf,
    generateOpportunity,
    updateSettings,
    toggleWatchlist,
    updateWatchlistItem,
    removeWatchlistItem,
    viewOpportunity,
    trackInPortfolio,
    updatePortfolioItem,
    removePortfolioItem,
    exportPortfolioPdf,
    testWhatsApp,
  };
}
